import { FC, useEffect, useState } from "react";
import classes from "./workTgsWidget.module.scss";
import { IWorkTg, workTgService } from "../../../../entities/workTg";
import { MyButton } from "../../../../shared/ui/button";
import { MyInput } from "../../../../shared/ui/input";
import { LoaderSpinner } from "../../../../shared/ui/spinner";
import { Modal } from "../../../../shared/ui/modal";
import { useGlobalLoadingActions } from "../../../../entities/globalLoading";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { FEATURES, hasFeature, useMyActions } from "../../../../entities/my";
import { MyError } from "../../../../shared/lib/error/MyError";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { SearchItems } from "../../../../features/searchItems";
import { useAppSelector } from "../../../../app/store/store";
import { DropDownListSelected } from "../../../../shared/ui/dropDownSelected";
import { IItem } from "../../../../shared/model/types";
import { idmService } from "../../../../entities/idm";

export const WorkTgsWidget: FC = () => {
    const getWorkTgName = (item: IWorkTg) => item.name || `TG #${item.id}`

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [workTgs, setWorkTgs] = useState<IWorkTg[]>([])
    const [workTgsSearch, setWorkTgsSearch] = useState<Array<IWorkTg & {name: string}>>([])
    const [createValue, setCreateValue] = useState<string>('')
    const [editValue, setEditValue] = useState<string>('')
    const [editingWorkTg, setEditingWorkTg] = useState<IWorkTg | null>(null)
    const [deletingWorkTg, setDeletingWorkTg] = useState<IWorkTg | null>(null)
    const [replacementWorkTgId, setReplacementWorkTgId] = useState<number | null>(null)
    const [deleteError, setDeleteError] = useState('')
    const [isDeleting, setIsDeleting] = useState(false)
    const [error, setError] = useState<string>('')

    const {setIsLoading: setGlobalLoading} = useGlobalLoadingActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()
    const {my} = useAppSelector(s => s.myReducer)
    const canList = hasFeature(my, FEATURES.WORK_TGS.LIST)
    const canCreate = hasFeature(my, FEATURES.WORK_TGS.CREATE)
    const canUpdate = hasFeature(my, FEATURES.WORK_TGS.UPDATE)
    const canDelete = hasFeature(my, FEATURES.WORK_TGS.DELETE)

    const syncSearch = (items: IWorkTg[]) => {
        setWorkTgsSearch(items.map(item => ({...item, name: getWorkTgName(item)})))
    }

    const handleError = (e: unknown, defaultMessage: string) => {
        console.log(e)
        if(e instanceof AuthError){
            setIsAuth(false)
            setGlobalMessage({message: e.message, type: 'error'})
        }
        else if(e instanceof MyError){
            setGlobalMessage({message: e.message, type: 'error'})
        }
        else{
            setGlobalMessage({message: defaultMessage, type: 'error'})
        }
    }

    const loadWorkTgs = async () => {
        try{
            setIsLoading(true)
            const items = await workTgService.getAll()
            const sortedItems = items.sort((a, b) => a.name.localeCompare(b.name))
            setWorkTgs(sortedItems)
            syncSearch(sortedItems)
        }
        catch(e){
            handleError(e, 'Ошибка при получении списка рабочих ТГ')
        }
        finally{
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if(canList){
            loadWorkTgs()
        }
        else{
            setIsLoading(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canList])

    const onCreate = async () => {
        if(!createValue.trim()){
            setError('Введите название рабочего ТГ')
            return
        }

        try{
            setGlobalLoading(true)
            const item = await workTgService.create(createValue.trim())
            setWorkTgs(current => {
                const next = [...current, item].sort((a, b) => a.name.localeCompare(b.name))
                syncSearch(next)
                return next
            })
            setCreateValue('')
            setError('')
            setGlobalMessage({message: 'Рабочий ТГ успешно создан', type: 'ok'})
        }
        catch(e){
            handleError(e, 'Ошибка при создании рабочего ТГ')
        }
        finally{
            setGlobalLoading(false)
        }
    }

    const onEditOpen = (item: IWorkTg) => {
        setEditingWorkTg(item)
        setEditValue(getWorkTgName(item))
        setError('')
    }

    const onEditClose = (open: boolean) => {
        if(!open){
            setEditingWorkTg(null)
            setEditValue('')
            setError('')
        }
    }

    const onEditSave = async () => {
        if(!editingWorkTg){
            return
        }
        if(!editValue.trim()){
            setError('Введите название рабочего ТГ')
            return
        }

        try{
            setGlobalLoading(true)
            const item = await workTgService.update(editingWorkTg.id, editValue.trim())
            setWorkTgs(current => {
                const next = current
                    .map(workTg => workTg.id === item.id ? item : workTg)
                    .sort((a, b) => a.name.localeCompare(b.name))
                syncSearch(next)
                return next
            })
            onEditClose(false)
            setGlobalMessage({message: 'Рабочий ТГ успешно обновлен', type: 'ok'})
        }
        catch(e){
            handleError(e, 'Ошибка при обновлении рабочего ТГ')
        }
        finally{
            setGlobalLoading(false)
        }
    }

    const openDeleteModal = (workTg: IWorkTg) => {
        setDeletingWorkTg(workTg)
        setReplacementWorkTgId(null)
        setDeleteError('')
    }

    const closeDeleteModal = () => {
        if(isDeleting) return
        setDeletingWorkTg(null)
        setReplacementWorkTgId(null)
        setDeleteError('')
    }

    const getDeleteErrorMessage = (error: unknown) => {
        const message = error instanceof Error ? error.message : ''
        if(message.includes('replacement work TG is already assigned to another team')){
            return 'Выбранный рабочий TG уже относится к другой команде. Выберите другой TG или сначала измените команду.'
        }
        if(message.includes('replacement work TG already has curator assignments for the same shifts')){
            return 'Для выбранного TG уже есть назначения кураторов на те же смены. Сначала разрешите конфликт назначений.'
        }
        return message || 'Не удалось удалить рабочий TG. Попробуйте ещё раз.'
    }

    const onDelete = async () => {
        if(!deletingWorkTg || isDeleting){
            return
        }

        if(!replacementWorkTgId || replacementWorkTgId === deletingWorkTg.id){
            setDeleteError('Выберите другой рабочий TG')
            return
        }

        try{
            setIsDeleting(true)
            setDeleteError('')
            await workTgService.delete(deletingWorkTg.id, replacementWorkTgId)
            setWorkTgs(current => {
                const next = current.filter(item => item.id !== deletingWorkTg.id)
                syncSearch(next)
                return next
            })
            setDeletingWorkTg(null)
            setReplacementWorkTgId(null)
            idmService.invalidateStaffListCache()
            setGlobalMessage({message: 'Рабочий ТГ успешно удален', type: 'ok'})
            void loadWorkTgs()
        }
        catch(e){
            if(e instanceof AuthError){
                setIsAuth(false)
            }
            setDeleteError(getDeleteErrorMessage(e))
        }
        finally{
            setIsDeleting(false)
        }
    }

    const replacementWorkTgItems: IItem[] = deletingWorkTg
        ? workTgs
            .filter(item => item.id !== deletingWorkTg.id)
            .map(item => ({id: item.id, name: `${getWorkTgName(item)} · ID ${item.id}`}))
        : []

    return (
        <section className={classes.container}>
            {canCreate && <section className={classes.create}>
                <section className={classes.createTitle}>Создать новый рабочий ТГ</section>
                <section className={classes.createForm}>
                    <section className={classes.input}>
                        <MyInput
                            value={createValue}
                            setValue={setCreateValue}
                            setError={setError}
                            error={error}
                            placeholder="Например: tg_math_admin"
                        />
                    </section>
                    <section className={classes.createButton}>
                        <MyButton onClick={onCreate} error={error}>
                            Создать
                        </MyButton>
                    </section>
                </section>
            </section>}
            <section className={classes.search}>
                <SearchItems
                    placeholder="Введите название рабочего ТГ"
                    items={workTgs.map(item => ({...item, name: getWorkTgName(item)}))}
                    setItems={setWorkTgsSearch}
                />
            </section>
            {
                isLoading
                    ?
                <section className={classes.loader}><LoaderSpinner /></section>
                    :
                <section className={classes.list}>
                    {
                        workTgsSearch.length > 0
                            ?
                        workTgsSearch.map(item => (
                            <section key={item.id} className={classes.row}>
                                <section className={classes.info}>
                                    <span className={classes.itemId}>ID: {item.id}</span>
                                    <span>{getWorkTgName(item)}</span>
                                </section>
                                <section className={classes.actions}>
                                    {canUpdate && <MyButton onClick={() => onEditOpen(item)}>Редактировать</MyButton>}
                                    {canDelete && <MyButton onClick={() => openDeleteModal(item)}>Удалить</MyButton>}
                                </section>
                            </section>
                        ))
                            :
                        <section className={classes.empty}>Рабочих ТГ пока нет</section>
                    }
                </section>
            }
            <Modal open={Boolean(editingWorkTg)} setOpen={onEditClose}>
                <section className={classes.modal}>
                    <section className={classes.modalTitle}>Редактировать рабочий ТГ</section>
                    <MyInput
                        value={editValue}
                        setValue={setEditValue}
                        setError={setError}
                        error={error}
                    />
                    <section className={classes.modalAction}>
                        <MyButton onClick={onEditSave} error={error}>Сохранить</MyButton>
                    </section>
                </section>
            </Modal>
            <Modal open={Boolean(deletingWorkTg)} setOpen={(open) => !open && closeDeleteModal()}>
                <section className={classes.modal}>
                    <section className={classes.modalTitle}>Удалить рабочий TG «{deletingWorkTg ? getWorkTgName(deletingWorkTg) : ''}»?</section>
                    <p className={classes.deleteWarning}>
                        Ученики, исполнители, сотрудники, занятия, графики, пробные уроки, события и назначения рабочего TG будут перенесены на выбранный TG. Исходный TG будет удалён.
                    </p>
                    {replacementWorkTgItems.length > 0 ? <>
                        <span className={classes.replacementLabel}>Перенести данные в</span>
                        <DropDownListSelected
                            items={replacementWorkTgItems}
                            selectedIdItems={replacementWorkTgId ? [replacementWorkTgId] : []}
                            onSelected={(item) => (selected) => {
                                setReplacementWorkTgId(selected ? Number(item.id) : null)
                                setDeleteError('')
                            }}
                            isLoading={false}
                            plainSelected
                            search
                            searchPlaceholder="Поиск рабочего TG"
                            placeholder="Выберите рабочий TG"
                            emptyText="Рабочие TG не найдены"
                        />
                    </> : <p className={classes.unavailableReplacement}>Для удаления сначала создайте другой рабочий TG для переноса данных.</p>}
                    {deleteError && <section className={classes.deleteError}>*{deleteError}</section>}
                    <section className={classes.deleteActions}>
                        <MyButton onClick={closeDeleteModal} disabled={isDeleting}>Отмена</MyButton>
                        <button
                            type="button"
                            className={classes.dangerButton}
                            onClick={onDelete}
                            disabled={!replacementWorkTgId || isDeleting || replacementWorkTgItems.length === 0}
                        >
                            {isDeleting ? 'Удаление…' : 'Удалить'}
                        </button>
                    </section>
                </section>
            </Modal>
        </section>
    )
}
